import { mapState } from 'vuex';
import store from '../store/index';
import { i18n } from '@/tools/i18n';

function createObjectFromArray(array) {
	return array.reduce((acc, el) => {
		acc[el[0]] = el[1];
		return acc;
	}, {});
}

function createObjectFromArrayByKey(array, key) {
	return array.reduce((acc, el) => {
		acc[el[key]] = el;
		return acc;
	}, {});
}

function zeroPad(num, size) {
	const s = `000000000${num}`;
	return s.substr(s.length - size);
}

function returnReadableTime(timeInSeconds) {
	const seconds = timeInSeconds % 60;
	const tmp = (timeInSeconds - seconds) / 60;
	const minutes = tmp % 60;
	const hours = (tmp - minutes) / 60;
	const secondsDisplay = zeroPad(Math.round(seconds), 2);
	return `${hours > 0 ? hours + ':' : ''}${hours ? zeroPad(minutes, 2) : minutes}:${secondsDisplay}`;
}

/**
 * Method to redirect an url to the datasource
 * @param {*} url
 * @param {*} dataSource url
 * @param { forceRAWURL } _options if input url is wrapped by "url()" it prevents rewrapping in the output
 */
const mediaFolderRegexp = /^media-[a-f\d]{24}$/i;
function setURLToDataSource(_url, _dataSource, _options) {
	const dataSource = _dataSource ? _dataSource : store.state.dataSourceURL;
	const options = Object.assign(
		{
			forceRAWURL: false,
			checkForMediaFolder: false,
		},
		_options
	);
	let url = _url.trim();

	let hasUrl = /^url/.test(url);
	let hasKommas = false;
	if (hasUrl) {
		url = url.slice(4, url.length - 1);
		hasKommas = /^"/.test(url);
		if (hasKommas) url = url.slice(1, url.length - 1);
	}

	// prevent output url wrapping if options.forceRAWURL
	hasUrl = hasUrl && !options.forceRAWURL;
	hasKommas = hasKommas && !options.forceRAWURL;
	if (/^\.\./.test(url)) {
		return `${hasUrl ? 'url(' : ''}${hasKommas ? '"' : ''}${url.replace(
			'../',
			`${dataSource.replace(/\/*$/, '')}/apps/`
		)}${hasKommas ? '"' : ''}${hasUrl ? ')' : ''}`;
	}

	let foundApps = false;
	const parts = url.split('/');
	const result = [];

	for (const part of parts) {
		if (options.checkForMediaFolder) {
			if (mediaFolderRegexp.test(part)) {
				// used for older ios devices
				foundApps = true;
				result.push('apps');
			}
		} else {
			if (part === 'apps') {
				foundApps = true;
			}
		}
		if (foundApps) {
			result.push(part);
		}
	}

	url = dataSource + result.join('/');

	return hasUrl ? `url(${url})` : url;
}
/**
 * Method return value of the current language and if it is not avaible it takse fallback language
 * @param {*} object languageObject has strucute f.e. { de: 'Titel', en:'Title', languageIsoName : value }
 * @param {*} state
 */
function getLangValue(object, state) {
	// logger.log(`getLangValue for ${language}:${defaultLanguage}`, object);

	if (typeof object !== 'object') return object; // if language value is nubmer, string,etc return
	const language = state.persistent.language;
	const defaultLanguage = state.defaultLanguage;
	return object[language]
		? object[language]
		: object[defaultLanguage]
		? object[defaultLanguage]
		: object[Object.keys(object)[0]];
}
function getLanguageContentWithFallback(languageContentArray, state) {
	const properties = Object.keys(languageContentArray[0]);
	return properties.reduce(
		(acc, property) =>
			Object.assign(acc, { [property]: getValueLanguageConent(languageContentArray, property, state) }),
		{}
	);
}
/**
 * This method is used on languageContent arrays to get language sensitive value
 * @param {*} array every array entry is an obejct with fields languageISO and key
 * @param {*} key is f.e. : title, description, cover
 * @param {*} state
 */
function getValueLanguageConent(array, key, state) {
	return getLangValue(
		array.reduce((acc, content) => {
			acc[content.languageISO] = content[key];
			return acc;
		}, {}),
		state
	);
}

function getLangLanguageConent(array, state) {
	return getLangValue(
		array.reduce((acc, value) => {
			acc[value.languageISO] = value;
			return acc;
		}, {}),
		state
	);
}

function getMultipleLangValues(object, props, state) {
	return props.reduce((acc, prop) => {
		acc[prop] = getLangValue(object[prop], state);
		return acc;
	}, {});
}

function getDataSourceURL() {
	const path = window.document.location.pathname.split('/');
	const appPath = path.slice(0, path.indexOf('apps'));

	return `${window.document.location.origin}${appPath.join('/')}/`;
}

async function waitForNativeinterface() {
	return new Promise(async resolve => {
		while (!window.nativeInterface) {
			console.log('native Interface not up wait for it!');
			await sleep(100);
		}
		resolve();
	});
}

function toLeafLetGeoForms(geoform) {
	geoform.coords = geoform.coords.map(coords => {
		return {
			lat: coords[1],
			lng: coords[0],
		};
	});
	return geoform;
}

function sleep(time) {
	return new Promise(resolve => {
		setTimeout(resolve, time);
	});
}

function isIOS() {
	return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function mapStateNested(statePath = '', props = []) {
	if (typeof statePath === 'string') {
		statePath = statePath.split('.');
	}

	function getNestedState(state) {
		return statePath.reduce((acc, field) => acc[field], state);
	}

	return mapState(
		props.reduce((acc, prop) => {
			acc[prop] = state => getNestedState(state)[prop];
			return acc;
		}, {})
	);
}

function detectLeftButton(event) {
	if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
		return false;
	} else if ('buttons' in event) {
		return event.buttons === 1;
	} else if ('which' in event) {
		return event.which === 1;
	} else {
		return event.button == 1 || event.type == 'click';
	}
}

function paddingLeft(paddingString, value) {
	return String(paddingString + value).slice(-paddingString.length);
}

function capitalizeFirstLetter(string) {
	if (!string) return string;
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function getWeekDayName(dayOfWeek) {
	return isNaN(dayOfWeek)
		? null
		: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
}

export function isValidUuid(string) {
	return /[0-9a-z]{24}/.test(string);
}

'August', 'September', 'October', 'November', 'December';
function getMonthName(month) {
	return isNaN(month)
		? null
		: [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
		  ][month];
}
/**
 * catchFocus is used to workaround restriction on ios devices setting focus via javascript
 * it is only allowed in click events but it is allowed to move the focus.
 * catchFocus in click event move focus on target input field after rendering of input field
 */
function catchFocus() {
	document.getElementById('focusCatcher').focus();
}

function toClassSave(string) {
	return string.replace(/[^0-9a-zA-Z\-]+/g, '');
}

const regexp = /^[a-f\d]{24}$/i;
function isDataBaseID(value) {
	return regexp.test(value);
}


export function equalRouteParams(a, b) {
	return (
		!!(a && b) &&
		a.exhibitionID === b.exhibitionID &&
		((!a.tourID && !b.tourID) || a.tourID === b.tourID) &&
		a.articleID === b.articleID
	);
}

export function shallowEqual(a, b, keys) {
	if (a === b) {
		return true;
	}

	if (typeof a !== typeof b || typeof a !== 'object') {
		return false;
	}

	const aKeys = keys || Object.keys(a);
	const bKeys = keys || Object.keys(b);

	if (aKeys.length !== bKeys.length) {
		return false;
	}

	for (const key of aKeys) {
		if (a[key] !== b[key]) {
			return false;
		}
	}

	return true;
}

function generateUUID() {
	let d = Date.now();
	return 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
		const r = ((d + Math.random() * 16) % 16) | 0;
		d = Math.floor(d / 16);
		return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
	});
}

function getFigcaptionLabel(post) {
	const showElements = post
		.getAttribute('elements-hidden')
		.split(',')
		.reduce((acc, part) => {
			const array = part.split(':');
			acc[array[0]] = array[1] === 'true';
			return acc;
		}, {});
	const $title = showElements['element__caption__headline']
		? post.querySelector('.element__caption__headline').textContent
		: '';
	const $description = showElements['element__caption__text']
		? post.querySelector('.element__caption__text').textContent
		: '';
	const $time = showElements['element__caption__time']
		? post.querySelector('.element__caption__time').textContent
		: '';
	const $attribution = showElements['element__caption__attribution']
		? post.querySelector('.element__caption__attribution').textContent
		: '';
	return `${$title} ${$description} ${$time ? `${i18n.t('duration')} ${$time}` : ''} ${$attribution}`;
}
function getFigcaptionTitle(post) {
	const showElements = post
		.getAttribute('elements-hidden')
		.split(',')
		.reduce((acc, part) => {
			const array = part.split(':');
			acc[array[0]] = array[1] === 'true';
			return acc;
		}, {});
	const $title = showElements['element__caption__headline']
		? post.querySelector('.element__caption__headline').textContent
		: '';
	const $time = showElements['element__caption__time']
		? post.querySelector('.element__caption__time').textContent
		: '';
	return `${$title} ${$time ? `${i18n.t('duration')} ${$time}` : ''}`;
}

export {
	toClassSave,
	catchFocus,
	capitalizeFirstLetter,
	waitForNativeinterface,
	createObjectFromArray,
	createObjectFromArrayByKey,
	setURLToDataSource,
	returnReadableTime,
	getLangValue,
	getLanguageContentWithFallback,
	getLangLanguageConent,
	getMultipleLangValues,
	getValueLanguageConent,
	getDataSourceURL,
	toLeafLetGeoForms,
	sleep,
	isIOS,
	mapStateNested,
	detectLeftButton,
	paddingLeft,
	getWeekDayName,
	getMonthName,
	isDataBaseID,
	generateUUID,
	getFigcaptionLabel,
	getFigcaptionTitle,
};
